/**
 * TractorBeam.ts
 *
 * Visual representation of the tractor beam.
 * Spawns below the player when Z is held, plays C3 animations, and despawns when released.
 *
 * Uses data from DataManagerWeapon which merges:
 * - weapons-c3data.json (dimensions, animations from C3)
 * - weapons-overrides.json (gameplay values)
 */

import Phaser from 'phaser';
import {
  DataManagerWeapon,
  TractorDefinition,
} from '../weapons/DataManagerWeapon';
import type { CollisionSystem } from '../systems/CollisionSystem';
import type { Entity } from './base/Entity';
import { LayerDepths } from '../constants/enums';

export interface TractorBeamConfig {
  scene: Phaser.Scene;
  /** The player entity to attach to */
  player: Phaser.GameObjects.Sprite;
}

/**
 * TractorBeam - Visual beam effect attached to player
 */
export class TractorBeam extends Phaser.GameObjects.Sprite {
  /** Visual offset to extend beam past collision zone to reach the grass */
  private static readonly GROUND_VISUAL_OFFSET = 10;

  private player: Phaser.GameObjects.Sprite;
  private isBeamActive: boolean = false;
  private currentDefinition: TractorDefinition;

  // Animation state
  private beamState: 'idle' | 'starting' | 'holding' | 'stopping' = 'idle';

  // Pulse animation
  private pulseTime: number = 0;

  // Tractor blockers (entities that block the beam)
  private tractorBlockers: Set<Entity> = new Set();

  constructor(config: TractorBeamConfig) {
    // Get default definition (must be called after DataManagerWeapon.load())
    const defaultDef = DataManagerWeapon.getTractor('punytractorbeam');
    if (!defaultDef) {
      throw new Error('[TractorBeam] DataManagerWeapon not loaded or punytractorbeam not found. Call DataManagerWeapon.load() before creating TractorBeam.');
    }

    super(config.scene, config.player.x, config.player.y, defaultDef.texture);

    this.player = config.player;
    this.currentDefinition = defaultDef;

    // Add to scene
    config.scene.add.existing(this);

    // Initial setup from C3 data
    this.setOrigin(defaultDef.originX, defaultDef.originY);
    // Depth visual (debajo del ovni/jugador)
    this.setDepth(defaultDef.visualDepth ?? LayerDepths.TractorBeam);
    this.setVisible(false);
    this.setActive(false);
    this.setScale(defaultDef.scale);

    // Setup animation listeners
    this.setupAnimationListeners();

    // Listen for tractor beam events
    this.setupEventListeners();
  }


  /**
   * Calculate beam length until nearest solid below (ground TileSprite or blocker entity)
   */
  private calculateBeamLength(): number {
    const def = this.currentDefinition;
    const beamTop = this.player.y + def.offsetY;

    // Get Y of nearest solid below using CollisionSystem
    const collisionSystem = this.scene.registry.get('collisionSystem') as CollisionSystem | undefined;
    if (!collisionSystem) {
      console.error('[TractorBeam] CollisionSystem not found in registry');
      return 0;
    }
    if (def.ignoreSolids) {
      return this.scene.scale.height - beamTop;
    }

    // Solo suelo (TileSprite), no casas ni entidades sólidas
    const solidY = collisionSystem.getGroundYBelow(this.player.x, beamTop);
    if (solidY === null) {
      // No solid below - extend to bottom of screen as fallback
      return this.scene.scale.height - beamTop;
    }

    let effectiveLength = solidY + TractorBeam.GROUND_VISUAL_OFFSET - beamTop;

    // Check registered blockers (entities that block beam, may be above the ground)
    if (!def.ignoreBlockers) {
      for (const blocker of this.tractorBlockers) {
        if (!blocker.active) continue;

        // Check if blocker is within beam's X range
        const beamHalfWidth = (def.width * def.scale) / 2;
        const beamLeft = this.player.x - beamHalfWidth;
        const beamRight = this.player.x + beamHalfWidth;

        if (blocker.x >= beamLeft && blocker.x <= beamRight) {
          // Calculate top of blocker
          const blockerTop = blocker.y - blocker.displayHeight * blocker.originY;
          if (blockerTop > beamTop && blockerTop < beamTop + effectiveLength) {
            effectiveLength = Math.min(effectiveLength, blockerTop - beamTop);
          }
        }
      }
    }

    return Math.max(0, effectiveLength);
  }

  /**
   * Register an entity as a tractor blocker
   */
  registerBlocker(entity: Entity): void {
    this.tractorBlockers.add(entity);
  }

  /**
   * Unregister an entity as a tractor blocker
   */
  unregisterBlocker(entity: Entity): void {
    this.tractorBlockers.delete(entity);
  }

  /**
   * Setup animation event listeners
   */
  private setupAnimationListeners(): void {
    this.on('animationcomplete', this.onAnimationComplete, this);
  }

  /**
   * Handle animation complete
   */
  private onAnimationComplete(animation: Phaser.Animations.Animation): void {
    const def = this.currentDefinition;

    if (animation.key === `${def.id}_start`) {
      // Start animation finished -> transition to hold
      this.beamState = 'holding';
      this.setAlpha(1); // Ya succionando: opaco
      this.playAnimation('hold');
    } else if (animation.key === `${def.id}_fallback`) {
      // Fallback animation finished -> fully deactivate
      this.setVisible(false);
      this.setActive(false);
      this.isBeamActive = false;
      this.beamState = 'idle';
    }
  }

  /**
   * Play a tractor beam animation
   */
  private playAnimation(animName: 'start' | 'hold' | 'fallback'): void {
    const def = this.currentDefinition;
    // Animation key format: punytractorbeam_start, megatractorbeam_start, etc.
    const animKey = `${def.id}_${animName}`;

    // Check if animation exists in global AnimationManager
    if (this.scene.anims.exists(animKey)) {
      this.play(animKey);
    } else {
      console.warn(`[TractorBeam] Animation not found: ${animKey}`);
      // Show first frame as fallback
      const fallbackTexture = `${def.id}_${animName}_0`;
      if (this.scene.textures.exists(fallbackTexture)) {
        this.setTexture(fallbackTexture);
      }
    }
  }

  /**
   * Setup event listeners
   */
  private setupEventListeners(): void {
    this.scene.events.on('tractorBeamStarted', this.onStart, this);
    this.scene.events.on('tractorBeamStopped', this.onStop, this);

    // Update position after all updates to stay perfectly synced with player
    this.scene.events.on('postupdate', this.updatePosition, this);

    // Cleanup on scene events
    this.scene.events.once('shutdown', this.destroy, this);
    this.scene.events.once('destroy', this.destroy, this);
  }

  /**
   * Handle tractor beam start
   */
  private onStart(data?: { weapon?: { id?: string } }): void {
    // Get definition for this weapon
    const weaponId = data?.weapon?.id || 'punytractorbeam';
    const newDef = DataManagerWeapon.getTractor(weaponId) || DataManagerWeapon.getTractorBeamDefinition(weaponId);
    if (newDef) {
      this.currentDefinition = newDef;
    }
    const def = this.currentDefinition;

    // Set correct texture if changed
    if (this.texture.key !== def.texture) {
      this.setTexture(def.texture);
    }

    // Apply definition values
    this.setOrigin(def.originX, def.originY);
    this.setScale(def.scale);
    // Actualizar depth si el tractor cambia
    this.setDepth(def.visualDepth ?? LayerDepths.TractorBeam);

    // Mostrar rayo
    this.setVisible(true);
    this.setActive(true);
    this.isBeamActive = true;
    this.beamState = 'starting';

    // Play start animation
    this.setAlpha(0.6); // Arranca semi-transparente
    this.playAnimation('start');

    // Update position
    this.updatePosition();
  }

  /**
   * Handle tractor beam stop
   */
  private onStop(): void {
    if (!this.isBeamActive) return;

    this.beamState = 'stopping';

    // Play fallback (closing) animation
    this.playAnimation('fallback');
  }

  /**
   * Update position to follow player
   */
  private updatePosition(): void {
    if (!this.isBeamActive || !this.player || !this.player.active) return;

    const def = this.currentDefinition;
    this.setPosition(this.player.x, this.player.y + def.offsetY);
  }

  /**
   * Update - call every frame
   */
  update(time: number, delta: number): void {
    if (!this.isBeamActive) return;

    const def = this.currentDefinition;

    // Follow player
    this.updatePosition();

    // Calculate and apply beam length (stops at ground/blockers)
    const length = this.calculateBeamLength();
    const baseHeight = def.height;
    const newScaleY = length / baseHeight;
    this.setScale(def.scale, newScaleY);

    // Only apply effects during hold state
    if (this.beamState === 'holding') {
      // Pulse effect
      this.pulseTime += delta * def.pulseSpeed;
      const pulse = 0.85 + Math.sin(this.pulseTime * 2) * 0.1;
      this.setAlpha(pulse);

      // Slight wobble
      const wobble = Math.sin(this.pulseTime * 3) * def.wobbleAmount;
      this.setRotation(Phaser.Math.DegToRad(wobble));
    }
  }

  /**
   * Check if beam is currently active
   */
  getIsActive(): boolean {
    // Solo activo para detección cuando el beam está completamente extendido
    return this.isBeamActive && this.beamState === 'holding';
  }

  /**
   * Get current definition
   */
  getDefinition(): TractorDefinition {
    return this.currentDefinition;
  }

  /**
   * Get the current beam length (for TractorBeamBehavior)
   */
  getBeamLength(): number {
    if (!this.isBeamActive) return 0;
    return this.calculateBeamLength();
  }

  /**
   * Get the beam bounding box in world coordinates (for overlap detection)
   */
  getBeamBounds(): { x: number; y: number; width: number; height: number } {
    if (!this.isBeamActive) {
      return { x: 0, y: 0, width: 0, height: 0 };
    }

    const def = this.currentDefinition;
    const beamWidth = def.width * def.scale;
    const beamHeight = this.calculateBeamLength();

    return {
      x: this.player.x - beamWidth / 2,
      y: this.player.y + def.offsetY,
      width: beamWidth,
      height: beamHeight,
    };
  }

  /**
   * Force stop the beam
   */
  forceStop(): void {
    this.onStop();
  }

  /**
   * Cleanup
   */
  destroy(fromScene?: boolean): void {
    // Prevent double destroy
    if (this.isBeamDestroyed) return;
    this.isBeamDestroyed = true;

    // Remove event listeners
    this.scene?.events?.off('tractorBeamStarted', this.onStart, this);
    this.scene?.events?.off('tractorBeamStopped', this.onStop, this);
    this.scene?.events?.off('postupdate', this.updatePosition, this);
    this.off('animationcomplete', this.onAnimationComplete, this);

    // Kill tweens
    this.scene?.tweens?.killTweensOf(this);

    super.destroy(fromScene);
  }

  private isBeamDestroyed = false;
}
